import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import './IBChatbot.css';

const EMPTY_PROVISIONS = {
  available: false,
  provisions: [],
  rules: [],
  notes: [],
  verified_sections: [],
  unverified_sections: [],
};

const COURT_FILTERS = [
  { key: 'all', short: 'All' },
  { key: 'supreme_court', short: 'SC' },
  { key: 'high_court', short: 'HC' },
  { key: 'nclat', short: 'NCLAT' },
  { key: 'nclt', short: 'NCLT' },
];

const PROV_TABS = [
  { key: 'all', short: 'All' },
  { key: 'acts', short: 'Acts' },
  { key: 'rules', short: 'Rules' },
];

const LOADING_STAGES = [
  'Understanding your query...',
  'Searching IBC provisions...',
  'Finding matching judgments...',
];

const NOT_AVAILABLE = 'Not available in the indexed document.';

/* ------------------------------------------------- history (server, per-user)
   Persistence lives on the backend, scoped to the authenticated Firebase
   user. localStorage ('ibc_history_v1') is NO LONGER used as history. */
const SEARCH_TOP_CASES = 24;
const COURT_FULL_NAME = {
  all: 'court',
  supreme_court: 'Supreme Court',
  high_court: 'High Court',
  nclat: 'NCLAT',
  nclt: 'NCLT',
};

const courtGroupOf = (name) => {
  const s = String(name || '').toLowerCase();
  if (s.includes('supreme')) return 'supreme_court';
  if (s.includes('high court')) return 'high_court';
  if (s.includes('nclat')) return 'nclat';
  if (s.includes('nclt')) return 'nclt';
  return 'other';
};

/* Firebase ID token → Authorization header for the history API.
   Reuses the EXISTING Firebase login (MyAccount page). Returns null when
   nobody is logged in or when the logged-in user is no longer `expectedUid`
   (protects against a queued op running after an account switch). */
const historyHeaders = async (expectedUid) => {
  const u = auth.currentUser;
  const currentUid = u ? u.uid : null;
  if (expectedUid !== undefined && expectedUid !== currentUid) return null;
  if (!u) return null;
  try {
    const token = await u.getIdToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  } catch (error) {
    return null;
  }
};

/* server row → the shape the sidebar and openHistory already understand */
const deserializeHistoryItem = (row) => {
  const m = (row && row.messages) || {};
  return {
    id: row.id,
    title: row.title || 'Untitled chat',
    pinned: !!row.pinned,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    messageCount: row.message_count || 0,
    ...m,
    summaryContexts: row.selected_contexts || [],
    queries: Array.isArray(m.queries) && m.queries.length ? m.queries : (m.query ? [m.query] : []),
  };
};

/* local conversation snapshot → { messages, selected_contexts } for the API
   (summary contexts are stored in their own column, not duplicated) */
const serializeHistoryItem = (snap, extra = {}) => {
  const { summaryContexts, ...rest } = snap || {};
  return {
    messages: { ...rest, ...extra },
    selected_contexts: Array.isArray(summaryContexts) ? summaryContexts : [],
  };
};

const titleFrom = (q) => {
  const t = String(q || '').trim().replace(/\s+/g, ' ');
  if (!t) return 'New conversation';
  return t.length > 42 ? `${t.slice(0, 42).trimEnd()}…` : t;
};

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== NOT_AVAILABLE;

/* bounded one-paragraph clip so Panel 3 never dumps raw judgment text */
const clip = (text, max = 420) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const stop = Math.max(cut.lastIndexOf('.'), cut.lastIndexOf(';'));
  return stop > max * 0.55 ? cut.slice(0, stop + 1).trim() : `${cut.trim()}…`;
};

/* pull a short, de-duplicated Section list out of a free-text field */
const parseSectionList = (raw, cap = 10) => {
  const out = [];
  const seen = new Set();
  const re = /section\s*(\d+\s*[a-z]?)/gi;
  let m;
  const str = String(raw || '');
  while (out.length < cap && (m = re.exec(str)) !== null) {
    const num = m[1].replace(/\s+/g, '');
    const key = num.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(`Section ${num}`);
  }
  return out;
};

const SelectIcon = () => (
  <svg className="ibc-add-btn-icon" viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const SelectedIcon = () => (
  <svg className="ibc-add-btn-icon" viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AddBtn = ({ active, onClick, title }) => (
  <button
    type="button"
    className={`ibc-add-btn ${active ? 'active' : ''}`}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title={title || (active ? 'Selected — click to remove' : 'Select this item')}
  >
    {active ? <SelectedIcon /> : <SelectIcon />}
    {active ? 'Selected' : 'Select'}
  </button>
);

const IBChatbot = () => {
  const [question, setQuestion] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [courtFilter, setCourtFilter] = useState('all');
  const [provTab, setProvTab] = useState('all');
  const [phase, setPhase] = useState('idle'); // idle | searching | done | error
  const [stage, setStage] = useState(LOADING_STAGES[0]);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [apiStatus, setApiStatus] = useState('connecting');

  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [caseDetails, setCaseDetails] = useState({});
  const [caseLoading, setCaseLoading] = useState(false);

  const [showHelp, setShowHelp] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true, provisions: true, facts: true, issue: true, finding: true, extracts: false, document: true,
  });

  /* ------------------------------------------------------ combined summary */
  const [summaryContexts, setSummaryContexts] = useState([]);
  const [summaryMessages, setSummaryMessages] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [summarySourcesUsed, setSummarySourcesUsed] = useState([]);
  const [summaryInstruction, setSummaryInstruction] = useState('');

  /* ------------------------------------------------------------- history */
  const [history, setHistory] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  /* existing Firebase auth (MyAccount / Navbar) — no new login system */
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  /* history item menu (rename / pin / delete) */
  const [menuForId, setMenuForId] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const caseListRef = useRef(null);
  const caseDetailsRef = useRef(null);
  const summaryMsgsRef = useRef(null);
  const summaryHistoryRef = useRef([]);
  const runIdRef = useRef(0);
  const stageTimerRef = useRef(null);
  const lastQueryRef = useRef('');
  const retryLockRef = useRef(false);
  const currentIdRef = useRef(null);
  const stateRef = useRef({});
  const queriesRef = useRef([]);          // queries of the open conversation
  const saveChainRef = useRef(Promise.resolve()); // serialises create/patch/open
  const prevUidRef = useRef(null);        // detect logout / account switch
  const inputRef = useRef(null);          // stable ref — focused only after a send
  const navigate = useNavigate();

  /* mirror latest render state so history saves never use stale closures */
  useEffect(() => {
    stateRef.current = {
      submittedQuery,
      result,
      courtFilter,
      provTab,
      selectedCaseId,
      caseDetails,
      summaryContexts,
      summaryMessages,
      summaryHistory: summaryHistoryRef.current,
      summarySourcesUsed,
      phase,
    };
  });

  /* ---------------------------------------------------------------- health */
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ibc/health`);
        if (!cancelled) setApiStatus(response.ok ? 'connected' : 'error');
      } catch (error) {
        if (!cancelled) setApiStatus('error');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [API_BASE_URL]);

  /* -------------------------------------------------------------- searching */
  /* ------------------------------------------- history persistence (server)
     Every create/patch/open runs through ONE serial queue so a record is
     never created twice and a pending save can't clobber a just-opened chat.
     Each job also carries the uid captured when it was queued, so a logout or
     account switch neutralises anything still pending. */
  const enqueueOp = useCallback((job) => {
    const uidAtEnqueue = auth.currentUser ? auth.currentUser.uid : null;
    saveChainRef.current = saveChainRef.current
      .then(() => job(uidAtEnqueue))
      .catch(() => {});
  }, []);

  /* called after each successful search: the FIRST query creates the record
     (§17), later queries update the SAME record (§16) */
  const saveConversation = useCallback((query, data) => {
    if (!queriesRef.current.includes(query)) queriesRef.current = [...queriesRef.current, query];
    const queries = [...queriesRef.current];

    enqueueOp(async (uid) => {
      const headers = await historyHeaders(uid);
      if (!headers) return; // not logged in → nothing is stored server-side
      const snap = { ...stateRef.current, updatedAt: Date.now() };
      const payload = serializeHistoryItem(snap, { query, queries, result: data });
      const id = currentIdRef.current;

      if (!id) {
        const res = await fetch(`${API_BASE_URL}/api/ibc/history`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ title: titleFrom(query), ...payload }),
        });
        if (!res.ok) return;
        const created = await res.json();
        currentIdRef.current = created.id;
        setCurrentId(created.id);
        setHistory((prev) => [created, ...prev]);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/ibc/history/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
      });
      if (res.status === 404) {
        currentIdRef.current = null;
        setCurrentId(null);
        setHistory((p) => p.filter((x) => x.id !== id));
        return;
      }
      if (!res.ok) return;
      setHistory((prev) => prev.map((it) => (
        it.id === id ? { ...it, message_count: queries.length, updated_at: Date.now() } : it
      )));
    });
  }, [API_BASE_URL, enqueueOp]);

  const runSearch = useCallback(async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    lastQueryRef.current = q;
    const runId = ++runIdRef.current;
    setPhase('searching');
    setErrorMessage('');
    setResult(null);
    setSelectedCaseId(null);
    setStage(LOADING_STAGES[0]);

    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    let idx = 0;
    stageTimerRef.current = setInterval(() => {
      idx = Math.min(idx + 1, LOADING_STAGES.length - 1);
      setStage(LOADING_STAGES[idx]);
    }, 900);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ibc/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // ONE retrieval for all courts — court tabs filter client-side afterwards
        body: JSON.stringify({ query: q, court: 'all', top_cases: SEARCH_TOP_CASES }),
      });
      if (!response.ok) throw new Error(`search failed (${response.status})`);
      const data = await response.json();
      if (runId !== runIdRef.current) return; // stale response
      setResult(data);
      setPhase('done');
      saveConversation(q, data);
    } catch (error) {
      if (runId !== runIdRef.current) return;
      setErrorMessage('Could not reach the IBC research service. Please try again.');
      setPhase('error');
    } finally {
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    }
  }, [API_BASE_URL, saveConversation]);

  /* Retry re-runs the last query; the phase-driven empty container flips
     error -> searching -> done/error in place (no new error div ever) */
  const handleRetry = useCallback(() => {
    if (phase !== 'error' || retryLockRef.current || !lastQueryRef.current) return;
    retryLockRef.current = true;
    Promise.resolve(runSearch(lastQueryRef.current)).finally(() => {
      retryLockRef.current = false;
    });
  }, [phase, runSearch]);

  /* court tabs are pure frontend filters over the preloaded result set */
  const handleCourtFilter = useCallback((key) => {
    setCourtFilter(key);
  }, []);

  /* ----------------------------------------------------------- case detail */
  const handleCaseClick = useCallback(async (caseItem) => {
    const caseId = caseItem.case_id;
    if (!caseId) return;
    setSelectedCaseId(caseId);
    if (caseDetails[caseId]) return;
    setCaseLoading(true);
    try {
      const q = encodeURIComponent((submittedQuery || question).trim());
      const response = await fetch(`${API_BASE_URL}/api/ibc/cases/${caseId}?q=${q}`);
      if (!response.ok) throw new Error('detail failed');
      const data = await response.json();
      setCaseDetails(prev => ({ ...prev, [caseId]: data }));
    } catch (error) {
      setCaseDetails(prev => ({
        ...prev,
        [caseId]: { found: false, error: 'Could not load this case. Please try again.' },
      }));
    } finally {
      setCaseLoading(false);
    }
  }, [API_BASE_URL, caseDetails, submittedQuery, question]);

  /* ---------------------------------------------------- summary selection */
  /* selection only adds context — NOTHING is generated until the user types
     in the one global input and presses Send (or hits the regenerate icon) */
  const toggleSummaryItem = useCallback((item) => {
    const exists = summaryContexts.some((c) => c.id === item.id);
    if (exists) {
      setSummaryContexts(summaryContexts.filter((c) => c.id !== item.id));
    } else {
      setSummaryContexts([...summaryContexts, item]);
      setSummaryOpen(true);
    }
  }, [summaryContexts]);

  const isSummarySelected = useCallback((id) => summaryContexts.some((c) => c.id === id), [summaryContexts]);

  /* chip × — removes ONLY that item from the summary context.
     The panel, the research cards and any generated summary all stay. */
  const removeSummaryContext = useCallback((id) => {
    setSummaryContexts((prev) => prev.filter((c) => c.id !== id));
  }, []);

  /* Generates the summary. Instructions come from the ONE global input bar
     (typed there and pressed Send); the header regenerate icon reuses the
     last instructions via summaryInstruction. */
  const generateSummary = useCallback(async (instructionOverride) => {
    if (summaryContexts.length === 0 || summaryLoading) return;

    const instruction = typeof instructionOverride === 'string'
      ? instructionOverride.trim()
      : summaryInstruction.trim();
    setSummaryInstruction(instruction);

    setSummaryOpen(true);
    setSummaryGenerated(true);
    setSummaryLoading(true);
    setSummaryMessages(instruction ? [{ id: `u-${Date.now()}`, sender: 'user', text: instruction }] : []);
    summaryHistoryRef.current = [];

    const selected_contexts = summaryContexts.map((c) => ({ source: c.source, content: c.content }));

    try {
      const res = await fetch(`${API_BASE_URL}/api/combined-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_contexts,
          follow_up: instruction || 'Combine these findings into a single coherent summary.',
          history: [],
        }),
      });
      const data = res.ok ? await res.json() : null;
      if (data && data.summary && !data.error) {
        const cleaned = data.summary.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
        setSummaryMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'assistant', text: cleaned }]);
        setSummarySourcesUsed(data.sources_used || []);
        summaryHistoryRef.current = [{ role: 'assistant', content: cleaned }];
      } else {
        const errText = (data && data.summary) || 'Error: Could not generate combined summary.';
        setSummaryMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'assistant', text: errText }]);
      }
    } catch (error) {
      setSummaryMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'assistant', text: 'Error: Failed to connect to combined summary service.' }]);
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryContexts, summaryLoading, summaryInstruction, API_BASE_URL]);

  /* RBI's Close summary — deletes the selection and the generated summary */
  const clearSummary = useCallback(() => {
    setSummaryContexts([]);
    setSummaryMessages([]);
    setSummarySourcesUsed([]);
    setSummaryGenerated(false);
    setSummaryLoading(false);
    setSummaryInstruction('');
    summaryHistoryRef.current = [];
    setSummaryOpen(false);
  }, []);

  /* combined follow-up — called by the ONE global input when Summary is open */
  const sendSummaryFollowup = useCallback(async (text) => {
    const q = (text || '').trim();
    if (!q || summaryLoading) return;
    if (summaryContexts.length === 0 || !summaryGenerated) return;

    setSummaryMessages((prev) => [...prev, { id: `u-${Date.now()}`, sender: 'user', text: q }]);
    setSummaryLoading(true);

    const selected_contexts = summaryContexts.map((c) => ({ source: c.source, content: c.content }));
    const history = [...summaryHistoryRef.current, { role: 'user', content: q }];

    try {
      const res = await fetch(`${API_BASE_URL}/api/combined-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_contexts, follow_up: q, history }),
      });
      const data = res.ok ? await res.json() : null;
      if (data && data.summary && !data.error) {
        const cleaned = data.summary.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
        setSummaryMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'assistant', text: cleaned }]);
        summaryHistoryRef.current = [...history, { role: 'assistant', content: cleaned }];
      } else {
        const errText = (data && data.summary) || 'No summary could be generated from the selected material.';
        setSummaryMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'assistant', text: errText }]);
      }
    } catch (error) {
      setSummaryMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, sender: 'assistant', text: 'Could not reach the summary service. Please try again.' },
      ]);
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryLoading, summaryContexts, summaryGenerated, API_BASE_URL]);

  /* ------------------------------------------------ ONE global input bar
     Summary CLOSED                -> normal IBC search
     Summary OPEN, not generated   -> typed text = summary instructions (Send generates)
     Summary OPEN, generated       -> combined-followup with the selected contexts  */
  const handleSubmit = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = (question || '').trim();

    if (summaryOpen) {
      if (apiStatus !== 'connected' || summaryLoading || summaryContexts.length === 0) return;
      setQuestion('');
      if (!summaryGenerated) {
        generateSummary(text); // empty text -> default "combine these findings" instruction
        if (inputRef.current) inputRef.current.focus();
        return;
      }
      if (!text) return;
      sendSummaryFollowup(text);
      if (inputRef.current) inputRef.current.focus();
      return;
    }

    if (!text || apiStatus !== 'connected' || phase === 'searching') return;
    // capture the exact query first, then clear the input for the loading state
    setSubmittedQuery(text);
    setQuestion('');
    runSearch(text);
    if (inputRef.current) inputRef.current.focus(); // ready for the next message
  }, [question, apiStatus, phase, summaryOpen, summaryLoading, summaryContexts, summaryGenerated, generateSummary, sendSummaryFollowup, runSearch]);

  /* Enter = send, Shift+Enter = new line */
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (summaryMsgsRef.current) {
      summaryMsgsRef.current.scrollTop = summaryMsgsRef.current.scrollHeight;
    }
  }, [summaryMessages, summaryLoading, summaryOpen]);

  /* ------------------------------------------------------------- utilities */
  /* reset the workspace only — history entries are never deleted */
  const resetWorkspace = useCallback(() => {
    currentIdRef.current = null;
    setCurrentId(null);
    queriesRef.current = [];
    runIdRef.current += 1;
    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    setQuestion('');
    setSubmittedQuery('');
    setResult(null);
    setPhase('idle');
    setErrorMessage('');
    setSelectedCaseId(null);
    setCaseDetails({});
    setCaseLoading(false);
    setCourtFilter('all');
    setProvTab('all');
    setSummaryContexts([]);
    setSummaryMessages([]);
    setSummarySourcesUsed([]);
    setSummaryGenerated(false);
    setSummaryInstruction('');

    summaryHistoryRef.current = [];
    setSummaryOpen(false);
  }, []);

  const handleRefresh = resetWorkspace;

  /* reopen a stored conversation — full data is fetched lazily when the item
     is clicked (§20); the sidebar only ever holds metadata */
  const openHistory = useCallback((item) => {
    if (!item) return;
    enqueueOp(async (uid) => {
      const headers = await historyHeaders(uid);
      if (!headers) return;
      const res = await fetch(`${API_BASE_URL}/api/ibc/history/${item.id}`, { headers });
      if (res.status === 404) {
        setHistory((prev) => prev.filter((x) => x.id !== item.id));
        return;
      }
      if (!res.ok) return;
      const it = deserializeHistoryItem(await res.json());

      currentIdRef.current = it.id;
      setCurrentId(it.id);
      queriesRef.current = it.queries || [];
      runIdRef.current += 1;
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
      setQuestion('');
      setSubmittedQuery(it.submittedQuery || it.query || '');
      setResult(it.result || null);
      setPhase(it.result ? 'done' : 'idle');
      setErrorMessage('');
      setCourtFilter(it.courtFilter || 'all');
      setProvTab(it.provTab || 'all');
      setSelectedCaseId(it.selectedCaseId || null);
      setCaseDetails(it.caseDetails || {});
      setCaseLoading(false);
      setSummaryContexts(it.summaryContexts || []);
      setSummaryMessages(it.summaryMessages || []);
      setSummarySourcesUsed(it.summarySourcesUsed || []);
      setSummaryGenerated((it.summaryMessages || []).length > 0);
      setSummaryInstruction('');

      summaryHistoryRef.current = it.summaryHistory || [];
      setSummaryOpen(false);
    });
  }, [API_BASE_URL, enqueueOp]);

  /* auto-save the open conversation to ITS server record (only once one
     exists — opening IBC or New Chat never creates a record, §17/§18) */
  useEffect(() => {
    if (!user || !currentIdRef.current || phase !== 'done') return;
    const payload = serializeHistoryItem({ ...stateRef.current, updatedAt: Date.now() });
    enqueueOp(async (uid) => {
      const id = currentIdRef.current;
      if (!id) return;
      const headers = await historyHeaders(uid);
      if (!headers) return;
      const res = await fetch(`${API_BASE_URL}/api/ibc/history/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
      });
      if (res.status === 404) {
        currentIdRef.current = null;
        setCurrentId(null);
        setHistory((p) => p.filter((x) => x.id !== id));
      }
    });
  }, [result, phase, summaryContexts, summaryMessages, selectedCaseId, caseDetails, courtFilter, provTab, currentId, user, API_BASE_URL, enqueueOp]);

  /* -------------------------------------------- history item actions (⋮) */
  const closeHistMenu = useCallback(() => {
    setMenuForId(null);
    setMenuPos(null);
  }, []);

  /* fixed positioning so the menu is never clipped by the sidebar */
  const openHistMenu = useCallback((e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_W = 150;
    const MENU_H = 94;
    const GAP = 6;
    let top = r.bottom + GAP;
    if (top + MENU_H > window.innerHeight - 8) top = Math.max(8, r.top - MENU_H - GAP);
    let left = r.right - MENU_W;
    if (left < 8) left = 8;
    if (left + MENU_W > window.innerWidth - 8) left = window.innerWidth - MENU_W - 8;
    setMenuPos({ top, left });
    setMenuForId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    if (!menuForId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeHistMenu(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuForId, closeHistMenu]);

  const togglePinHistory = useCallback((id) => {
    const target = history.find((it) => it.id === id);
    const nextPinned = !(target && target.pinned);
    setHistory((prev) => prev.map((it) => (it.id === id ? { ...it, pinned: nextPinned } : it)));
    closeHistMenu();
    enqueueOp(async (uid) => {
      const headers = await historyHeaders(uid);
      if (!headers) return;
      const res = await fetch(`${API_BASE_URL}/api/ibc/history/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ pinned: nextPinned }),
      });
      if (res.status === 404) setHistory((prev) => prev.filter((it) => it.id !== id));
    });
  }, [history, closeHistMenu, API_BASE_URL, enqueueOp]);

  const startRenameHistory = useCallback((item) => {
    if (!item) return;
    setRenamingId(item.id);
    setRenameValue(item.title || '');
    closeHistMenu();
  }, [closeHistMenu]);

  const commitRenameHistory = useCallback(() => {
    const title = renameValue.trim();
    const id = renamingId;
    setRenamingId(null);
    setRenameValue('');
    if (!id || !title) return;
    setHistory((prev) => prev.map((it) => (it.id === id ? { ...it, title } : it)));
    enqueueOp(async (uid) => {
      const headers = await historyHeaders(uid);
      if (!headers) return;
      const res = await fetch(`${API_BASE_URL}/api/ibc/history/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ title }),
      });
      if (res.status === 404) setHistory((prev) => prev.filter((it) => it.id !== id));
    });
  }, [renamingId, renameValue, API_BASE_URL, enqueueOp]);

  const cancelRenameHistory = useCallback(() => {
    setRenamingId(null);
    setRenameValue('');
  }, []);

  const requestDeleteHistory = useCallback((id) => {
    setDeleteId(id);
    closeHistMenu();
  }, [closeHistMenu]);

  const confirmDeleteHistory = useCallback(() => {
    const id = deleteId;
    setDeleteId(null);
    if (!id) return;
    setHistory((prev) => prev.filter((it) => it.id !== id));
    if (currentIdRef.current === id) resetWorkspace();
    enqueueOp(async (uid) => {
      const headers = await historyHeaders(uid);
      if (!headers) return;
      await fetch(`${API_BASE_URL}/api/ibc/history/${id}`, { method: 'DELETE', headers });
    });
  }, [deleteId, resetWorkspace, API_BASE_URL, enqueueOp]);

  /* --------------------------------------------------- auth (Firebase, existing)
     Reuses the login from MyAccount page. On logout everything loaded for the
     previous account is wiped before another account's data can load. */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  /* uid change: wipe first (logout or account switch), then load the new
     account's history — nothing from the previous user is ever kept visible */
  useEffect(() => {
    const uid = user ? user.uid : null;
    if (prevUidRef.current && prevUidRef.current !== uid) {
      resetWorkspace();
      setHistory([]);
      closeHistMenu();
      setRenamingId(null);
      setRenameValue('');
      setDeleteId(null);
    }
    prevUidRef.current = uid;
    if (!uid) return;

    let cancelled = false;
    (async () => {
      const headers = await historyHeaders(uid);
      if (!headers || cancelled) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/ibc/history`, { headers });
        if (res.status === 401) { if (!cancelled) setHistory([]); return; }
        if (!res.ok) return;
        const rows = await res.json();
        if (!cancelled) setHistory(Array.isArray(rows) ? rows : []);
      } catch (error) { /* network hiccup: keep what we have */ }
    })();
    return () => { cancelled = true; };
  }, [user, API_BASE_URL, resetWorkspace, closeHistMenu]);

  useEffect(() => {
    if (caseDetailsRef.current && selectedCaseId) caseDetailsRef.current.scrollTop = 0;
  }, [selectedCaseId]);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  /* ------------------------------------------------------- context builders */
  const provisionContext = (p) => ({
    id: `prov:${p.section}`,
    source: 'Acts & Rules — Section',
    label: `${p.section}`,
    content: [
      `${p.section} (${provisions.act || 'Insolvency and Bankruptcy Code, 2016'})`,
      p.explanation ? `Provision: ${p.explanation}` : '',
      p.why_relevant ? `Why relevant: ${p.why_relevant}` : '',
      p.supporting_documents ? `Cited in ${p.supporting_documents.toLocaleString()} indexed document(s).` : '',
    ].filter(Boolean).join('\n'),
  });

  const ruleContext = (r) => ({
    id: `rule:${r.rule}`,
    source: 'Acts & Rules — Rule',
    label: `${r.rule}`,
    content: [
      `${r.rule}`,
      r.explanation ? `Provision: ${r.explanation}` : '',
      r.why_relevant ? `Why relevant: ${r.why_relevant}` : '',
      r.supporting_documents ? `Referenced in ${r.supporting_documents.toLocaleString()} indexed document(s).` : '',
    ].filter(Boolean).join('\n'),
  });

  const caseContext = (c) => ({
    id: `case:${c.case_id}`,
    source: 'Matching Case Law',
    label: c.case_name || 'Case',
    content: [
      `Case: ${c.case_name || 'N/A'}`,
      `Court: ${c.court || 'N/A'}`,
      c.date ? `Date: ${c.date}` : '',
      c.case_no ? `Case No: ${c.case_no}` : '',
      c.citation ? `Citation: ${c.citation}` : '',
      c.matched_sections && c.matched_sections.length ? `Relevant provisions: ${c.matched_sections.join(', ')}` : '',
      c.relevance_reason ? `Relevance: ${c.relevance_reason}` : '',
      c.snippet ? `Extract: ${c.snippet}` : '',
    ].filter(Boolean).join('\n'),
  });

  const detailSectionContext = (key, detail) => {
    const head = `Case: ${detail.case_name || 'N/A'} (${detail.court || 'Court unknown'})`;
    const bodies = {
      overview: [
        `Case Number: ${detail.case_no || NOT_AVAILABLE}`,
        `Date: ${detail.date || NOT_AVAILABLE}`,
        `Parties: ${detail.parties || NOT_AVAILABLE}`,
        `Citation: ${detail.citation || NOT_AVAILABLE}`,
        `Bench / Judge: ${detail.judge || NOT_AVAILABLE}`,
      ],
      provisions: [
        `Sections discussed: ${detail.sections || NOT_AVAILABLE}`,
        `Acts / Code: ${detail.acts || NOT_AVAILABLE}`,
        `Rules / Regulations: ${detail.rules || NOT_AVAILABLE}`,
      ],
      aspects: ['facts', 'legal_issue', 'findings']
        .filter((k) => detail.aspects && detail.aspects[k])
        .map((k) => `${k === 'legal_issue' ? 'Legal issue' : k === 'findings' ? 'Judgment / findings' : 'Relevant facts'}: ${detail.aspects[k].text}`),
      facts: detail.aspects && detail.aspects.facts ? [`Relevant facts: ${detail.aspects.facts.text}`] : [],
      issue: detail.aspects && detail.aspects.legal_issue ? [`Legal issue: ${detail.aspects.legal_issue.text}`] : [],
      finding: detail.aspects && detail.aspects.findings
        ? [`Court's finding: ${detail.aspects.findings.text}`]
        : [`Court's finding: The indexed material does not contain enough of the final judgment to provide a reliable short summary of the final decision.`],
      extracts: (detail.excerpts || []).map((ex) => `[Page ${ex.page_start ?? '?'}] ${ex.text}`),
      document: [
        `File: ${detail.document?.file_name || NOT_AVAILABLE}`,
        `Pages: ${detail.document?.pages ?? NOT_AVAILABLE}`,
        `Extraction: ${detail.document?.extraction_status || NOT_AVAILABLE}${detail.document?.ocr_used ? ' (OCR applied)' : ''}`,
      ],
    };
    const lines = (bodies[key] || []).filter(Boolean);
    if (!lines.length) return null;
    const shortName = (detail.case_name || 'Case').slice(0, 30);
    const secName = {
      overview: '', provisions: 'Provisions', aspects: 'Facts/Issue/Findings',
      facts: 'Facts', issue: 'Legal Issue', finding: "Court's Finding",
      extracts: 'Extracts', document: 'Document',
    }[key];
    return {
      id: `detail:${detail.case_id}:${key}`,
      source: `Case Details — ${{
        aspects: 'Facts/Issue/Findings',
        facts: 'Facts',
        issue: 'Legal Issue',
        finding: "Court's Finding",
      }[key] || key.charAt(0).toUpperCase() + key.slice(1)}`,
      label: `Case Details — ${shortName}${secName && key !== 'overview' ? ` · ${secName}` : ''}`,
      content: [head, ...lines].join('\n'),
    };
  };

  const addDetailSection = (key) => {
    if (!selectedDetail || !selectedDetail.found) return;
    const item = detailSectionContext(key, selectedDetail);
    if (item) toggleSummaryItem(item);
  };

  /* ---------------------------------------------------------------- derived */
  const provisions = result?.provisions || EMPTY_PROVISIONS;
  const cases = result?.cases || [];
  const matchedCount = result?.matched_case_count ?? 0;
  const selectedDetail = selectedCaseId ? caseDetails[selectedCaseId] : null;
  const selectedCard = cases.find((c) => c.case_id === selectedCaseId) || null;
  const caseAspects = (selectedDetail && selectedDetail.aspects) || null;
  const NO_FINDING = 'The indexed material does not contain enough of the final judgment to provide a reliable short summary of the final decision.';

  /* Panel 3 → CASE SUMMARY: grounded, clipped, built from indexed material only */
  const caseSummaryPoints = [];
  if (caseAspects && caseAspects.facts) caseSummaryPoints.push({ label: 'What happened', text: clip(caseAspects.facts.text, 380) });
  if (caseAspects && caseAspects.legal_issue) caseSummaryPoints.push({ label: 'Main issue', text: clip(caseAspects.legal_issue.text, 380) });
  caseSummaryPoints.push({
    label: "Court's finding",
    text: caseAspects && caseAspects.findings ? clip(caseAspects.findings.text, 380) : NO_FINDING,
  });
  if (selectedCard && selectedCard.relevance_reason) caseSummaryPoints.push({ label: 'Why this case matters', text: clip(selectedCard.relevance_reason, 300) });

  /* Panel 3 → KEY DETAILS: only fields that actually exist */
  const keyFields = selectedDetail ? [
    ['Case', selectedDetail.case_name],
    ['Court', selectedDetail.court],
    ['Date', selectedDetail.date],
    ['Case Number', selectedDetail.case_no],
    ['Bench / Judge', selectedDetail.judge],
    ['Parties', selectedDetail.parties],
    ['Citation', selectedDetail.citation],
  ].filter(([, v]) => hasValue(v)) : [];

  /* Panel 3 → RELEVANT PROVISIONS: query-relevant first, else short parsed list */
  const relevantSections = selectedDetail
    ? ((selectedCard && Array.isArray(selectedCard.matched_sections) && selectedCard.matched_sections.length)
      ? selectedCard.matched_sections.slice(0, 10)
      : parseSectionList(selectedDetail.sections, 10))
    : [];

  const rulesName = (provisions.rules && provisions.rules[0] && provisions.rules[0].act)
    || 'Rules / Regulations referenced in the indexed judgments';

  const showActs = provTab !== 'rules';
  const showRules = provTab !== 'acts';

  const nothingActs = provisions.provisions.length === 0;
  const nothingRules = (provisions.rules || []).length === 0;
  const nothingToShow = provTab === 'acts'
    ? nothingActs
    : provTab === 'rules'
      ? nothingRules
      : (nothingActs && nothingRules);

  const panel1Empty = phase === 'idle'
    ? 'Ask a question to find relevant Acts & Rules.'
    : phase === 'searching'
      ? stage
      : phase === 'error'
        ? errorMessage
        : !provisions.available
          ? 'No relevant provisions found in the indexed documents.'
          : nothingToShow
            ? 'Nothing retrieved under this tab for your question.'
            : null;

  /* one retrieval, grouped by court — tab clicks filter instantly (no API) */
  const allCases = result?.cases || [];
  const casesByCourt = {
    all: allCases,
    supreme_court: [],
    high_court: [],
    nclat: [],
    nclt: [],
    other: [],
  };
  allCases.forEach((c) => {
    const key = courtGroupOf(c.court);
    (casesByCourt[key] || casesByCourt.other).push(c);
  });
  const visibleCases = casesByCourt[courtFilter] || allCases;

  const panel2Empty = phase === 'idle'
    ? 'Matching cases will appear here.'
    : phase === 'searching'
      ? stage
      : phase === 'error'
        ? errorMessage
        : visibleCases.length === 0
          ? (courtFilter !== 'all'
            ? `No ${COURT_FULL_NAME[courtFilter]} case in this result set. Try the “All” tab.`
            : (result?.reason === 'below_relevance_floor'
              ? 'Nothing in the indexed corpus closely matches this question. Try rephrasing with IBC terms, a section number, or the party names.'
              : 'No closely matching cases found in the indexed case-law corpus.'))
          : null;

  /* combined-summary header/badge label — sources used, RBI-style */
  const summarySourceNames = (summarySourcesUsed.length > 0 ? summarySourcesUsed : summaryContexts.map((c) => c.source))
    .map((s) => String(s || '').split(' - ')[0].trim())
    .filter(Boolean);
  const summarySourcesLabel = summarySourceNames.length
    ? Array.from(new Set(summarySourceNames)).join(' + ')
    : (summaryContexts.length ? `${summaryContexts.length} selected` : 'Nothing selected');

  /* history sidebar: pinned first, then recent (existing order kept) */
  const pinnedItems = history.filter((it) => it.pinned);
  const recentItems = history.filter((it) => !it.pinned);
  const menuIsPinned = menuForId ? !!(history.find((it) => it.id === menuForId) || {}).pinned : false;
  const deleteItem = deleteId ? history.find((it) => it.id === deleteId) : null;

  const renderHistoryItem = (it) => {
    const renaming = renamingId === it.id;
    const msgCount = it.message_count || (it.queries && it.queries.length) || 1;
    return (
      <div
        key={it.id}
        className={`ibc-sidebar-item ${currentId === it.id ? 'active' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => { if (!renaming) openHistory(it); }}
        onKeyDown={(e) => {
          if (!renaming && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openHistory(it); }
        }}
        title={it.title}
      >
        {renaming ? (
          <div className="ibc-sidebar-rename" onClick={(e) => e.stopPropagation()}>
            <input
              className="ibc-sidebar-rename-input"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRenameHistory(); }
                else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelRenameHistory(); }
              }}
              onBlur={commitRenameHistory}
              aria-label="Chat name"
            />
            <button
              type="button"
              className="ibc-sidebar-rename-save"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitRenameHistory}
              title="Save name"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
            </button>
          </div>
        ) : (
          <>
            <span className="ibc-sidebar-item-title">
              {it.pinned && (
                <svg className="ibc-sidebar-pin-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 17v5" />
                  <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
                </svg>
              )}
              <span className="ibc-sidebar-item-text">{it.title}</span>
            </span>
            <span className="ibc-sidebar-item-meta">
              {msgCount} message{msgCount === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="ibc-sidebar-dots"
              title="Chat options"
              aria-label={`Options for ${it.title}`}
              onClick={(e) => openHistMenu(e, it.id)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  /* ------------------------------------------------------------------ render */
  return (
    <div className="ibc-page">

      {/* ── History sidebar (ChatGPT-style, collapsible) ── */}
      <aside className={`ibc-sidebar ${sidebarOpen ? 'open' : ''}`} aria-hidden={!sidebarOpen}>
        <div className="ibc-sidebar-head">
          <button className="ibc-sidebar-new" onClick={resetWorkspace}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Chat
          </button>
          <button className="ibc-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close history sidebar">&times;</button>
        </div>
        <div className="ibc-sidebar-label">History</div>
        <div className="ibc-sidebar-list">
          {!authReady ? (
            <div className="ibc-sidebar-empty">Loading…</div>
          ) : !user ? (
            <div className="ibc-sidebar-login">
              <p className="ibc-sidebar-login-text">Log in to view your chat history.</p>
              <button
                type="button"
                className="ibc-sidebar-login-btn"
                onClick={() => navigate('/my-account')}
              >
                Log in
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="ibc-sidebar-empty">Your conversations will appear here after your first search.</div>
          ) : (
            <>
              {pinnedItems.length > 0 && <div className="ibc-sidebar-group">Pinned</div>}
              {pinnedItems.map(renderHistoryItem)}
              {recentItems.length > 0 && <div className="ibc-sidebar-group">Recent</div>}
              {recentItems.map(renderHistoryItem)}
            </>
          )}
        </div>
      </aside>

      {/* ── history item menu (Rename / Pin / Delete) ── */}
      {menuForId && <div className="ibc-hist-menu-overlay" onMouseDown={closeHistMenu} />}
      {menuForId && menuPos && (
        <div className="ibc-hist-menu" style={{ top: menuPos.top, left: menuPos.left }} role="menu">
          <button type="button" className="ibc-hist-menu-item" role="menuitem" onClick={() => startRenameHistory(history.find((it) => it.id === menuForId))}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            Rename
          </button>
          <button type="button" className="ibc-hist-menu-item" role="menuitem" onClick={() => togglePinHistory(menuForId)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
            </svg>
            {menuIsPinned ? 'Unpin chat' : 'Pin chat'}
          </button>
          <div className="ibc-hist-menu-sep" />
          <button type="button" className="ibc-hist-menu-item danger" role="menuitem" onClick={() => requestDeleteHistory(menuForId)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* ── delete confirmation ── */}
      {deleteId && (
        <div className="ibc-confirm-overlay" onMouseDown={() => setDeleteId(null)}>
          <div className="ibc-confirm-card" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ibc-confirm-title">Delete this chat?</div>
            <div className="ibc-confirm-text" title={(deleteItem || {}).title}>{(deleteItem || {}).title}</div>
            <div className="ibc-confirm-actions">
              <button type="button" className="ibc-confirm-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" className="ibc-confirm-ok" onClick={confirmDeleteHistory}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="ibc-container">

        {/* ── Top Bar ── */}
        <div className="ibc-topbar">
          <div className="ibc-topbar-left">
            <button
              className="ibc-topbar-btn ibc-sidebar-toggle"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label={sidebarOpen ? 'Hide chat history' : 'Show chat history'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="9" y1="4" x2="9" y2="20"/></svg>
              {sidebarOpen ? 'Hide history' : 'History'}
            </button>
            <div className="ibc-logo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              IBC
            </div>
            <div className="ibc-topbar-title">Legal Research Assistant</div>
            <div className="ibc-badge">Indexed IBC case law &amp; provisions</div>
          </div>
          <div className="ibc-topbar-right">
            <div className={`ibc-connection ${apiStatus}`}>
              <span className="ibc-connection-dot" />
              {apiStatus === 'connected' ? 'Connected' : apiStatus === 'error' ? 'Disconnected' : 'Connecting'}
            </div>
            <button className="ibc-topbar-btn" onClick={() => setShowHelp(true)} aria-label="Help">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Help
            </button>
            <button className="ibc-topbar-btn" onClick={handleRefresh} aria-label="Refresh">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><line x1="20" y1="5" x2="20" y2="11"/><path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              Refresh
            </button>
          </div>
        </div>

        {/* ── Workspace ── */}
        <div className="ibc-workspace">

          {/* ── Combined Summary workspace (RBI-style: generates on open) ── */}
          {summaryOpen ? (
            <div className="ibc-summary ibc-summary--full">
              <div className="ibc-summary-fullhead">
                <div className="ibc-summary-fullhead-left">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <span className="ibc-summary-fulltitle">Combined Summary</span>
                  <span className="ibc-summary-sources-badge" title={summarySourcesLabel}>{summarySourcesLabel}</span>
                </div>
                <div className="ibc-summary-fullhead-right">
                  <button
                    type="button"
                    className="ibc-summary-iconbtn"
                    title="Copy summary"
                    onClick={() => { navigator.clipboard.writeText(summaryMessages.map((m) => m.text).join('\n\n')).catch(() => {}); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="ibc-summary-iconbtn"
                    title="Regenerate"
                    onClick={generateSummary}
                    disabled={summaryLoading || summaryContexts.length === 0}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="ibc-summary-iconbtn"
                    title="Collapse"
                    onClick={() => setSummaryOpen(false)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="ibc-summary-iconbtn ibc-summary-iconbtn--danger"
                    title="Close summary"
                    onClick={clearSummary}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="ibc-summary-context">
                <div className="ibc-summary-context-label">Selected material</div>
                <div className="ibc-summary-chips">
                  {summaryContexts.length === 0 ? (
                    <span className="ibc-summary-chips-empty">
                      No material selected. Select Acts, Rules, Cases or Case Details from the panels above.
                    </span>
                  ) : (
                    summaryContexts.map((c) => (
                      <span className="ibc-summary-chip" key={c.id} title={c.label}>
                        {c.label}
                        <button
                          type="button"
                          className="ibc-summary-chip-x"
                          onClick={() => removeSummaryContext(c.id)}
                          aria-label="Remove from summary"
                        >&times;</button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="ibc-summary-msgs" ref={summaryMsgsRef}>
                {summaryMessages.length === 0 && !summaryLoading && (
                  <div className="ibc-summary-empty">
                    No summary yet — review your selected material, then type optional
                    instructions in the input below and press Send. Your summary will appear here.
                  </div>
                )}
                {summaryLoading && summaryMessages.length === 0 ? (
                  <div className="ibc-summary-loading">
                    <div className="ibc-typing"><span /><span /><span /></div>
                    <span>Generating combined summary...</span>
                  </div>
                ) : (
                  <div className="ibc-summary-msglist">
                    {summaryMessages.map((m) => (
                      <div className={`ibc-summary-msg-row ${m.sender}`} key={m.id}>
                        {m.sender === 'user' ? (
                          <div className="ibc-summary-msg-user">{m.text}</div>
                        ) : (
                          <div className="ibc-summary-msg-assistant">
                            <div className="ibc-summary-avatar">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                            </div>
                            <div className="ibc-summary-msg-content">
                              <div className="ibc-summary-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                              </div>
                              <div className="ibc-summary-msg-actions">
                                <button
                                  type="button"
                                  className="ibc-summary-msg-action"
                                  onClick={() => { navigator.clipboard.writeText(m.text).catch(() => {}); }}
                                  title="Copy"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                  </svg>
                                  Copy
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {summaryLoading && (
                      <div className="ibc-summary-msg-row assistant">
                        <div className="ibc-summary-msg-assistant">
                          <div className="ibc-summary-avatar">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </div>
                          <div className="ibc-summary-msg-content">
                            <div className="ibc-typing"><span /><span /><span /></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
          <>
          {/* ── Three-Column Grid ── */}
          <div className="ibc-grid">

            {/* ── Panel 1: Acts & Rules ── */}
            <div className="ibc-src ibc-src--acts">
              <div className="ibc-src-header">
                <div className="ibc-src-title-row">
                  <div className="ibc-src-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div className="ibc-src-header-text">
                    <div className="ibc-src-title">1 · Relevant Acts &amp; Rules</div>
                    <div className="ibc-src-subtitle">Legal provisions related to your question</div>
                  </div>
                </div>
                <div className="ibc-src-filter-row">
                  <div className="ibc-src-tabs">
                    {PROV_TABS.map(t => (
                      <button
                        key={t.key}
                        className={`ibc-src-tab ${provTab === t.key ? 'active' : ''}`}
                        onClick={() => setProvTab(t.key)}
                      >{t.short}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ibc-src-body">
                <div className="ibc-scroll">
                  {panel1Empty ? (
                    <div className="ibc-empty">
                      <div className="ibc-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      </div>
                      <div className="ibc-empty-title">{phase === 'searching' ? 'Searching…' : phase === 'error' ? 'Search failed' : 'No provisions'}</div>
                      <div className="ibc-empty-desc">{panel1Empty}</div>
                      {phase === 'searching' && (
                        <div className="ibc-typing ibc-empty-typing"><span/><span/><span/></div>
                      )}
                      {phase === 'error' && (
                        <button type="button" className="ibc-retry-btn" onClick={handleRetry}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                          Retry
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="ibc-prov-list">
                      {showActs && (
                        <div className="ibc-prov-group">
                          <div className="ibc-prov-group-title">Acts</div>
                          <div className="ibc-prov-act">{provisions.act || 'Insolvency and Bankruptcy Code, 2016'}</div>
                          {provisions.provisions.map((p, i) => {
                            const item = provisionContext(p);
                            const active = isSummarySelected(item.id);
                            return (
                              <div className={`ibc-prov-card ${active ? 'selected' : ''}`} key={`s-${i}`}>
                                <div className="ibc-prov-head">
                                  <span className="ibc-prov-tag">{p.section}</span>
                                  <span className="ibc-prov-head-right">
                                    {p.supporting_documents > 0 && (
                                      <span className="ibc-prov-count">{p.supporting_documents.toLocaleString()} docs</span>
                                    )}
                                    <AddBtn active={active} onClick={() => toggleSummaryItem(item)} />
                                  </span>
                                </div>
                                <div className="ibc-prov-expl">{p.explanation}</div>
                                <div className="ibc-prov-why"><strong>Why relevant:</strong> {p.why_relevant}</div>
                              </div>
                            );
                          })}
                          {provisions.provisions.length === 0 && (
                            <div className="ibc-prov-empty">No sections retrieved for this tab.</div>
                          )}
                        </div>
                      )}

                      {showRules && (
                        <div className="ibc-prov-group">
                          <div className="ibc-prov-group-title">Rules</div>
                          <div className="ibc-prov-act ibc-prov-act--rules">{rulesName}</div>
                          {(provisions.rules || []).map((r, i) => {
                            const item = ruleContext(r);
                            const active = isSummarySelected(item.id);
                            return (
                              <div className={`ibc-prov-card ibc-prov-card--rule ${active ? 'selected' : ''}`} key={`r-${i}`}>
                                <div className="ibc-prov-head">
                                  <span className="ibc-prov-tag ibc-prov-tag--rule">{r.rule}</span>
                                  <span className="ibc-prov-head-right">
                                    {r.supporting_documents > 0 && (
                                      <span className="ibc-prov-count">{r.supporting_documents.toLocaleString()} docs</span>
                                    )}
                                    <AddBtn active={active} onClick={() => toggleSummaryItem(item)} />
                                  </span>
                                </div>
                                <div className="ibc-prov-expl">{r.explanation}</div>
                                <div className="ibc-prov-why"><strong>Why relevant:</strong> {r.why_relevant}</div>
                              </div>
                            );
                          })}
                          {(provisions.rules || []).length === 0 && (
                            <div className="ibc-prov-empty">No rules retrieved for this tab.</div>
                          )}
                        </div>
                      )}

                      {showActs && provisions.unverified_sections && provisions.unverified_sections.length > 0 && (
                        <div className="ibc-prov-warning">
                          Could not verify from the indexed documents: {provisions.unverified_sections.join(', ')}.
                        </div>
                      )}
                      {provisions.notes && provisions.notes.length > 0 && (
                        <div className="ibc-prov-notes">
                          {provisions.notes.map((n, i) => <div key={i}>· {n}</div>)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Panel 2: Matching Case Law ── */}
            <div className="ibc-src ibc-src--cases">
              <div className="ibc-src-header">
                <div className="ibc-src-title-row">
                  <div className="ibc-src-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                  <div className="ibc-src-header-text">
                    <div className="ibc-src-title">2 · Case Law</div>
                    <div className="ibc-src-subtitle">Cases matching your situation</div>
                  </div>
                </div>
                <div className="ibc-src-filter-row">
                  <div className="ibc-src-tabs">
                    {COURT_FILTERS.map(c => (
                      <button
                        key={c.key}
                        className={`ibc-src-tab ${courtFilter === c.key ? 'active' : ''}`}
                        onClick={() => handleCourtFilter(c.key)}
                        title={`${(casesByCourt[c.key] || []).length} ${COURT_FULL_NAME[c.key]} case$((casesByCourt[c.key] || []).length) === 1 ? '' : 's'} in this result set (no new search)`}
                      >{c.short}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ibc-src-body">
                <div className="ibc-scroll" ref={caseListRef}>
                  {panel2Empty ? (
                    <div className="ibc-empty">
                      <div className="ibc-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      </div>
                      <div className="ibc-empty-title">
                        {phase === 'searching' ? 'Searching…' : phase === 'error' ? 'Search failed' : 'No cases found'}
                      </div>
                      <div className="ibc-empty-desc">{panel2Empty}</div>
                      {phase === 'searching' && (
                        <div className="ibc-typing ibc-empty-typing"><span/><span/><span/></div>
                      )}
                      {phase === 'error' && (
                        <button type="button" className="ibc-retry-btn" onClick={handleRetry}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                          Retry
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="ibc-case-list">
                      <div className="ibc-case-count">
                        {courtFilter === 'all'
                          ? <>
                              {cases.length} case{cases.length === 1 ? '' : 's'} retrieved in one search
                              {matchedCount > 0 ? ` · ${matchedCount} closely matching` : ''}
                            </>
                          : `${visibleCases.length} ${COURT_FULL_NAME[courtFilter]} case${visibleCases.length === 1 ? '' : 's'} in this result set`}
                      </div>
                      {visibleCases.map((c, i) => {
                        const item = caseContext(c);
                        const active = isSummarySelected(item.id);
                        return (
                          <div
                            key={c.case_id || i}
                            className={`ibc-case-card ${selectedCaseId === c.case_id ? 'active' : ''} ${active ? 'selected' : ''}`}
                            onClick={() => handleCaseClick(c)}
                          >
                            <div className="ibc-case-card-top">
                              <div className="ibc-case-card-name">{c.case_name || `Case ${i + 1}`}</div>
                              <span className={`ibc-badge-rel ibc-badge-rel--${(c.relevance_label || '').split(' ')[0].toLowerCase()}`}>
                                {c.relevance_label}
                              </span>
                            </div>
                            <div className="ibc-case-card-meta">
                              <span className="ibc-case-card-court">{c.court || 'Court'}</span>
                              {c.date && <span>{c.date}</span>}
                              {c.case_no && <span>{c.case_no}</span>}
                            </div>
                            {c.matched_sections && c.matched_sections.length > 0 && (
                              <div className="ibc-case-card-sec">
                                Relevant: {c.matched_sections.join(' · ')}
                              </div>
                            )}
                            <div className="ibc-case-card-why">{c.relevance_reason}</div>
                            <div className="ibc-case-card-foot">
                              <span className="ibc-case-card-cta">View Case →</span>
                              <AddBtn active={active} onClick={() => toggleSummaryItem(item)} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Panel 3: Case Details ── */}
            <div className="ibc-src ibc-src--details">
              <div className="ibc-src-header">
                <div className="ibc-src-title-row">
                  <div className="ibc-src-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div className="ibc-src-header-text">
                    <div className="ibc-src-title">3 · Case Details</div>
                    <div className="ibc-src-subtitle">Quick summary first, then key details</div>
                  </div>
                </div>
              </div>
              <div className="ibc-src-body">
                <div className="ibc-scroll ibc-scroll--center" ref={caseDetailsRef}>
                  {caseLoading ? (
                    <div className="ibc-loading">
                      <div className="ibc-spinner" />
                      <span className="ibc-loading-text">Loading case details...</span>
                    </div>
                  ) : selectedCaseId && selectedDetail ? (
                    selectedDetail.found ? (
                      <div>
                        <div className="ibc-detail-section ibc-detail-section--summary">
                          <div className="ibc-detail-header ibc-detail-header--static">
                            <span>Case Summary</span>
                          </div>
                          <div className="ibc-detail-body">
                            {caseSummaryPoints.map((p) => (
                              <div className="ibc-case-summary-point" key={p.label}>
                                <div className="ibc-case-summary-label">{p.label}</div>
                                <p className="ibc-case-summary-text">{p.text}</p>
                              </div>
                            ))}
                            <div className="ibc-case-summary-note">
                              Summary based on the indexed judgment available in the case database. The original PDF
                              remains the authoritative source for the complete case.
                            </div>
                          </div>
                        </div>

                        {[
                          { key: 'overview', label: 'Key Details' },
                          { key: 'provisions', label: 'Relevant Provisions' },
                          { key: 'facts', label: 'Facts' },
                          { key: 'issue', label: 'Legal Issue' },
                          { key: 'finding', label: "Court's Finding" },
                          { key: 'extracts', label: 'Relevant Extracts' },
                          { key: 'document', label: 'Source Document' },
                        ].map((sec) => {
                          const ctx = detailSectionContext(sec.key, selectedDetail);
                          return (
                            <div className="ibc-detail-section" key={sec.key}>
                              <div
                                className="ibc-detail-header"
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleSection(sec.key)}
                                onKeyDown={(e) => { if (e.key === 'Enter') toggleSection(sec.key); }}
                              >
                                <span>{sec.label}</span>
                                <span className="ibc-detail-header-right">
                                  {ctx && (
                                    <AddBtn active={isSummarySelected(ctx.id)} onClick={() => addDetailSection(sec.key)} />
                                  )}
                                  <span className={`ibc-detail-chevron ${expandedSections[sec.key] ? 'open' : ''}`}>&#9654;</span>
                                </span>
                              </div>

                              {sec.key === 'overview' && expandedSections.overview && (
                                <div className="ibc-detail-body">
                                  {keyFields.length ? (
                                    keyFields.map(([label, value]) => (
                                      <div className="ibc-detail-field" key={label}>
                                        <div className="ibc-detail-label">{label}</div>
                                        <div className="ibc-detail-value">{value}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="ibc-detail-value">{NOT_AVAILABLE}</p>
                                  )}
                                </div>
                              )}

                              {sec.key === 'provisions' && expandedSections.provisions && (
                                <div className="ibc-detail-body">
                                  <div className="ibc-detail-field">
                                    <div className="ibc-detail-label">Sections</div>
                                    {relevantSections.length ? (
                                      <div className="ibc-prov-chips">
                                        {relevantSections.map((s) => (
                                          <span className="ibc-prov-chip" key={s}>{s}</span>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="ibc-detail-value">{NOT_AVAILABLE}</div>
                                    )}
                                  </div>
                                  {hasValue(selectedDetail.acts) && (
                                    <div className="ibc-detail-field">
                                      <div className="ibc-detail-label">Acts / Code</div>
                                      <div className="ibc-detail-value">{clip(selectedDetail.acts, 240)}</div>
                                    </div>
                                  )}
                                  {hasValue(selectedDetail.rules) && (
                                    <div className="ibc-detail-field">
                                      <div className="ibc-detail-label">Rules / Regulations</div>
                                      <div className="ibc-detail-value">{clip(selectedDetail.rules, 240)}</div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {sec.key === 'facts' && expandedSections.facts && (
                                <div className="ibc-detail-body">
                                  {caseAspects && caseAspects.facts ? (
                                    <>
                                      <p className="ibc-excerpt-text">{clip(caseAspects.facts.text, 520)}</p>
                                      <div className="ibc-excerpt-meta">Page {caseAspects.facts.page_start ?? '?'}</div>
                                    </>
                                  ) : (
                                    <p className="ibc-detail-value">{NOT_AVAILABLE}</p>
                                  )}
                                </div>
                              )}

                              {sec.key === 'issue' && expandedSections.issue && (
                                <div className="ibc-detail-body">
                                  {caseAspects && caseAspects.legal_issue ? (
                                    <>
                                      <p className="ibc-excerpt-text">{clip(caseAspects.legal_issue.text, 520)}</p>
                                      <div className="ibc-excerpt-meta">Page {caseAspects.legal_issue.page_start ?? '?'}</div>
                                    </>
                                  ) : (
                                    <p className="ibc-detail-value">{NOT_AVAILABLE}</p>
                                  )}
                                </div>
                              )}

                              {sec.key === 'finding' && expandedSections.finding && (
                                <div className="ibc-detail-body">
                                  {caseAspects && caseAspects.findings ? (
                                    <>
                                      <p className="ibc-excerpt-text">{clip(caseAspects.findings.text, 520)}</p>
                                      <div className="ibc-excerpt-meta">Page {caseAspects.findings.page_start ?? '?'}</div>
                                    </>
                                  ) : (
                                    <p className="ibc-excerpt-text">{NO_FINDING}</p>
                                  )}
                                </div>
                              )}

                              {sec.key === 'extracts' && expandedSections.extracts && (
                                <div className="ibc-detail-body">
                                  {selectedDetail.excerpts && selectedDetail.excerpts.length > 0 ? (
                                    selectedDetail.excerpts.map((ex, i) => (
                                      <div className="ibc-excerpt" key={ex.chunk_id || i}>
                                        <div className="ibc-excerpt-meta">
                                          Page {ex.page_start ?? '?'}
                                          {ex.page_end && ex.page_end !== ex.page_start ? `–${ex.page_end}` : ''}
                                        </div>
                                        <p className="ibc-excerpt-text">{ex.text}</p>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="ibc-excerpt-text">{NOT_AVAILABLE}</p>
                                  )}
                                </div>
                              )}

                              {sec.key === 'document' && expandedSections.document && (
                                <div className="ibc-detail-body">
                                  <div className="ibc-detail-field">
                                    <div className="ibc-detail-label">File</div>
                                    <div className="ibc-detail-value">{selectedDetail.document?.file_name || NOT_AVAILABLE}</div>
                                  </div>
                                  {selectedDetail.document?.pages != null && (
                                    <div className="ibc-detail-field">
                                      <div className="ibc-detail-label">Pages</div>
                                      <div className="ibc-detail-value">{selectedDetail.document.pages}</div>
                                    </div>
                                  )}
                                  {selectedDetail.source_document_endpoint ? (
                                    <div className="ibc-doc-actions">
                                      <a
                                        className="ibc-action-btn outline ibc-doc-link"
                                        href={`${API_BASE_URL}${selectedDetail.source_document_endpoint}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >View Original PDF</a>
                                      <a
                                        className="ibc-action-btn outline ibc-doc-link ghost"
                                        href={`${API_BASE_URL}${selectedDetail.source_document_endpoint}`}
                                        download
                                        onClick={(e) => e.stopPropagation()}
                                      >Download PDF</a>
                                    </div>
                                  ) : (
                                    <p className="ibc-detail-value">{NOT_AVAILABLE}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="ibc-empty">
                        <div className="ibc-empty-title">Case unavailable</div>
                        <div className="ibc-empty-desc">{selectedDetail.error || NOT_AVAILABLE}</div>
                      </div>
                    )
                  ) : (
                    <div className="ibc-empty ibc-empty--center">
                      <div className="ibc-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      </div>
                      <div className="ibc-empty-title">Select a case to view details</div>
                      <div className="ibc-empty-desc">Click a matching case in panel 2.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

          {summaryContexts.length > 0 && (
            <div className="ibc-summary-minimized">
              <div className="ibc-summary-minimized-left">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <span className="ibc-summary-minimized-title">Combined Summary</span>
                <span className="ibc-summary-minimized-badge" title={summarySourcesLabel}>{summarySourcesLabel}</span>
              </div>
              <div className="ibc-summary-minimized-right">
                <button type="button" className="ibc-summary-expand-btn" onClick={() => setSummaryOpen(true)} title="Expand summary">
                  Expand
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
                <button type="button" className="ibc-summary-close-btn" onClick={clearSummary} title="Close and delete summary">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </>
          )}

          {/* ── Global Search (the ONE input bar) ── */}
          <form className={`ibc-search ${summaryOpen ? 'ibc-search--summary' : ''}`} onSubmit={handleSubmit}>
            <div className="ibc-search-input-wrap">
              <span className="ibc-search-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              {summaryOpen && <span className="ibc-search-mode-badge">Summary</span>}
              <textarea
                ref={inputRef}
                className="ibc-search-input"
                rows={1}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  summaryOpen
                    ? (summaryGenerated
                      ? 'Ask a follow-up about this combined summary...'
                      : 'Tell me what you want included in the summary (optional)...')
                    : 'Describe your problem in your own words — English, Hindi or Hinglish…'
                }
                aria-label={summaryOpen ? 'Combined summary question' : 'Search query'}
              />
            </div>
            <button
              className="ibc-search-btn"
              type="submit"
              disabled={
                apiStatus !== 'connected'
                || (summaryOpen
                  ? (summaryLoading || summaryContexts.length === 0)
                  : phase === 'searching')
              }
            >
              {summaryOpen
                ? (summaryLoading ? 'Working…' : 'Send')
                : (phase === 'searching' ? 'Searching...' : apiStatus !== 'connected' ? 'Disconnected' : 'Search')}
            </button>
          </form>
          {summaryOpen && (
            <div className="ibc-search-hint">
              {summaryGenerated
                ? 'Answering from Combined Summary'
                : 'Summary not generated yet — type instructions below (optional) and press Send'}
            </div>
          )}

        </div>

        {/* ── Help Modal ── */}
        {showHelp && (
          <div className="ibc-help-overlay" onClick={() => setShowHelp(false)}>
            <div className="ibc-help-card" onClick={(e) => e.stopPropagation()}>
              <button className="ibc-help-close" onClick={() => setShowHelp(false)} aria-label="Close">&times;</button>
              <h3>How to Use</h3>
              <div className="ibc-help-steps">
                <div className="ibc-help-step"><div className="ibc-help-step-num">1</div><div className="ibc-help-step-text"><strong>Describe your problem</strong> — type it naturally. English, Hindi and Hinglish all work.</div></div>
                <div className="ibc-help-step"><div className="ibc-help-step-num">2</div><div className="ibc-help-step-text"><strong>Acts &amp; Rules</strong> — provisions related to your question, with Acts / Rules tabs.</div></div>
                <div className="ibc-help-step"><div className="ibc-help-step-num">3</div><div className="ibc-help-step-text"><strong>Matching Case Law</strong> — cases from the indexed corpus. Use the court filter to narrow retrieval.</div></div>
                <div className="ibc-help-step"><div className="ibc-help-step-num">4</div><div className="ibc-help-step-text"><strong>Case Details</strong> — click any case to read its details and original document.</div></div>
                <div className="ibc-help-step"><div className="ibc-help-step-num">5</div><div className="ibc-help-step-text"><strong>Combined Summary</strong> — add items with “Select”, then type optional instructions in the bottom input and press <strong>Send</strong> to generate it. Ask follow-ups in the same input.</div></div>
              </div>
              <p className="ibc-help-note">
                This tool summarises an indexed IBC case-law database for legal research.
                It is not legal advice.
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default IBChatbot;
